import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldStar, Broom, Plus, Trash, Crown, Sun, MoonStars } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useDialog } from "../components/Dialog";

const CATS = {
  SECURITY: { label: "Security", icon: ShieldStar, accent: "cyan" },
  HOUSEKEEPING: { label: "Housekeeping", icon: Broom, accent: "lime" },
};

export default function RosterMaster() {
  const { category: paramCat } = useParams();
  const navigate = useNavigate();
  const { confirm } = useDialog();
  const category = (paramCat || "SECURITY").toUpperCase();
  const cat = CATS[category] || CATS.SECURITY;
  const isSecurity = category === "SECURITY";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [shift, setShift] = useState("DAY");
  const [supervisor, setSupervisor] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/society-staff?category=${category}`);
      setItems(data.items || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); setName(""); setSupervisor(false); setShift("DAY"); setErr(null); /* eslint-disable-next-line */ }, [category]);

  async function add(e) {
    e?.preventDefault();
    if (name.trim().length < 2) { setErr("Enter a name."); return; }
    setBusy(true); setErr(null);
    try {
      await api.post("/api/society-staff", {
        name: name.trim(), category,
        shift: isSecurity ? shift : null,
        isSupervisor: supervisor,
      });
      setName(""); setSupervisor(false);
      await load();
    } catch {
      setErr("Could not add. Please try again.");
    } finally { setBusy(false); }
  }

  async function remove(s) {
    if (!(await confirm({ title: "Remove personnel?", message: `${s.name} will be removed from the roster.`, confirmText: "Remove", danger: true }))) return;
    await api.delete(`/api/society-staff/${s.id}`); load();
  }

  const Icon = cat.icon;

  return (
    <div className="space-y-4 font-sans" data-testid="roster-master">
      <div>
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white/70 hover:text-white">
          <ArrowLeft size={16} weight="bold" /> Back
        </button>
        <div className="mt-2 flex items-center gap-2">
          <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${cat.accent === "lime" ? "bg-neon-lime/15 text-neon-lime" : "bg-neon-cyan/15 text-neon-cyan"}`}><Icon size={18} weight="duotone" /></div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-white/50">Master</p>
            <h1 className="font-display text-2xl font-extrabold text-white">{cat.label} Roster</h1>
          </div>
        </div>
      </div>

      {/* Add */}
      <form onSubmit={add} className="card space-y-3 p-4" data-testid="roster-add">
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">Add {cat.label.toLowerCase()} personnel</p>
        <div>
          <label className="label">Name</label>
          <input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ramesh Patil" data-testid="roster-name" />
        </div>
        {isSecurity && (
          <div>
            <label className="label">Shift</label>
            <select className="field !py-3" value={shift} onChange={(e) => setShift(e.target.value)} data-testid="roster-shift">
              <option value="DAY" className="bg-ink-800">Day shift</option>
              <option value="NIGHT" className="bg-ink-800">Night shift</option>
            </select>
          </div>
        )}
        {!isSecurity && (
          <p className="text-[11px] text-white/45">Housekeeping runs a single default shift — no shift selection needed.</p>
        )}
        <label className="flex items-center gap-2 text-[13px] text-white/80">
          <input type="checkbox" checked={supervisor} onChange={(e) => setSupervisor(e.target.checked)} data-testid="roster-supervisor" />
          <Crown size={14} weight="fill" className="text-neon-amber" /> Mark as supervisor
        </label>
        {err && <p className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{err}</p>}
        <button className="btn-primary w-full" disabled={busy || name.trim().length < 2} data-testid="roster-submit">
          <Plus size={16} weight="bold" className="mr-1.5" /> {busy ? "Adding…" : `Add to ${cat.label.toLowerCase()}`}
        </button>
      </form>

      {/* List */}
      <div>
        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">{cat.label} staff · {items.length}</p>
        {loading ? (
          <div className="card p-8 text-center text-sm text-white/55">Loading…</div>
        ) : items.length === 0 ? (
          <div className="card p-8 text-center text-sm text-white/55">No {cat.label.toLowerCase()} staff yet.</div>
        ) : (
          <div className="space-y-2">
            {items.map((s) => {
              const RowIcon = isSecurity ? (s.shift === "NIGHT" ? MoonStars : Sun) : Broom;
              return (
                <div key={s.id} className="card flex items-center gap-3 p-3" data-testid={`roster-row-${s.id}`}>
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${cat.accent === "lime" ? "bg-neon-lime/15 text-neon-lime" : "bg-neon-cyan/15 text-neon-cyan"}`}>
                    <RowIcon size={18} weight="duotone" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="truncate font-display text-[14px] font-semibold text-white">{s.name}</p>
                      {s.isSupervisor && <Crown size={11} weight="fill" className="text-neon-amber" />}
                    </div>
                    <p className="text-[10px] uppercase tracking-wider text-white/50">{s.category}{s.shift ? ` · ${s.shift}` : ""}</p>
                  </div>
                  <button onClick={() => remove(s)} data-testid={`roster-remove-${s.id}`}
                    className="rounded-full bg-white/5 p-2 text-white/45 hover:text-neon-pink"><Trash size={14} weight="bold" /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
